import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  Drawer,
  Toolbar,
  AppBar,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Button,
  IconButton,
  Stack,
  FormControl,
  Select,
  MenuItem,
  TextField,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  Bolt as BoltIcon,
  Dashboard as DashboardIcon,
  ViewKanban as KanbanIcon,
  ListAlt as ListIcon,
  CalendarToday as CalendarTodayIcon,
  BackupTable as BacklogIcon,
  Timeline as SprintsIcon,
  Assignment as TicketsIcon,
  Label as LabelIcon,
  AccountTree as WorkflowIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const drawerWidth = 200;

const TABS = [
  { key: "Insights", icon: <DashboardIcon /> },
  { key: "Board", icon: <KanbanIcon /> },
  { key: "List", icon: <ListIcon /> },
  { key: "Calendar", icon: <CalendarTodayIcon /> },
  { key: "Backlog", icon: <BacklogIcon /> },
  { key: "Sprints", icon: <SprintsIcon /> },
  { key: "All Tickets", icon: <TicketsIcon /> },
  { key: "Labels", icon: <LabelIcon /> },
  { key: "Workflow", icon: <WorkflowIcon /> },
  { key: "Settings", icon: <SettingsIcon /> },
];

export default function DashboardLayout({ 
  children, 
  currentTab, 
  onTabChange, 
  timeRange, 
  onTimeRangeChange,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  onRefresh,
  isLoading = false,
  onNewTicket
}) {
  const isMdUp = useMediaQuery("(min-width:768px)");
  const [mobileOpen, setMobileOpen] = useState(false);

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar sx={{ gap: 1, opacity: 0.8 }}>
        <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
          <BoltIcon fontSize="small" />
        </Avatar>
        <Typography variant="h6" fontWeight={600} fontSize="1rem">Agile</Typography>
      </Toolbar>
      <Divider />
      <List sx={{ py: 1 }}>
        {TABS.map((t) => (
          <ListItemButton 
            key={t.key} 
            selected={currentTab === t.key} 
            onClick={() => onTabChange(t.key)} 
            sx={{ 
              borderRadius: 1.5, 
              mx: 0.5, 
              mb: 0.25,
              opacity: 0.7,
              "&:hover": { opacity: 1 },
              "&.Mui-selected": { opacity: 1 }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{t.icon}</ListItemIcon>
            <ListItemText primary={t.key} primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }} />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ mt: "auto", p: 1.5 }}>
        <Button 
          fullWidth 
          variant="outlined" 
          size="small"
          startIcon={<AddIcon />}
          onClick={onNewTicket}
          sx={{ 
            opacity: 0.8,
            "&:hover": { opacity: 1 }
          }}
        >
          New Ticket
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Top AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          color: "text.primary",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {!isMdUp && (
            <IconButton 
              edge="start" 
              onClick={() => setMobileOpen((x) => !x)}
              aria-label="Toggle navigation menu"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Dashboard — {currentTab}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Time Range Selector */}
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small">
              <Select
                value={timeRange}
                onChange={(e) => onTimeRangeChange(e.target.value)}
                displayEmpty
                variant="outlined"
                startAdornment={<CalendarIcon sx={{ mr: 1, fontSize: 20 }} />}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="Yesterday">Yesterday</MenuItem>
                <MenuItem value="Last 7 days">Last 7 days</MenuItem>
                <MenuItem value="Last 30 days">Last 30 days</MenuItem>
                <MenuItem value="Custom">Custom</MenuItem>
              </Select>
            </FormControl>
            
            {/* Custom Date Range Picker */}
            {timeRange === "Custom" && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => onCustomStartDateChange(e.target.value)}
                  variant="outlined"
                  sx={{ minWidth: 140 }}
                />
                <Typography variant="body2" color="text.secondary">to</Typography>
                <TextField
                  size="small"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => onCustomEndDateChange(e.target.value)}
                  variant="outlined"
                  sx={{ minWidth: 140 }}
                />
              </Stack>
            )}
            
            {/* Refresh Button */}
            <IconButton
              size="small"
              disabled={isLoading}
              sx={{ 
                border: 1, 
                borderColor: "divider",
                color: isLoading ? "text.disabled" : "text.secondary",
                "&:hover": {
                  backgroundColor: isLoading ? "transparent" : "action.hover",
                  color: isLoading ? "text.disabled" : "primary.main"
                }
              }}
              onClick={() => {
                if (onRefresh && !isLoading) {
                  onRefresh();
                }
              }}
            >
              <RefreshIcon 
                fontSize="small" 
                sx={{
                  animation: isLoading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" }
                  }
                }}
              />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="navigation">
        {/* Mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ 
            display: { xs: "block", md: "none" }, 
            "& .MuiDrawer-paper": { 
              boxSizing: "border-box", 
              width: drawerWidth 
            } 
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop */}
        <Drawer
          variant="permanent"
          sx={{ 
            display: { xs: "none", md: "block" }, 
            "& .MuiDrawer-paper": { 
              boxSizing: "border-box", 
              width: drawerWidth,
              height: "100vh",
              overflow: "hidden",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              borderRight: "1px solid rgba(0, 0, 0, 0.08)"
            } 
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "2px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.15)",
            borderRadius: "1px",
            minHeight: "20px",
            "&:hover": {
              background: "rgba(0, 0, 0, 0.25)",
            }
          }
        }}
      >
         <Toolbar />
         <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
           {children}
         </Box>
       </Box>
    </Box>
  );
}
